import React, { useEffect, useRef, useState } from "react";
import { useGridApiRef } from "@mui/x-data-grid";
import { Box, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useLocalStorage, { writeStorage } from "@rehooks/local-storage";
import ColumnsSection from "features/Collections/components/ColumnSection";
import { useSelector } from "react-redux";
import CollectionTableComponent from "features/SourceSearch/components/collection-table-component";
import { useColumns } from "features/SourceSearch/components/generate-columns";
import { publicCollectionsListColumns, publicCollectionsListdefaultVisibilityModel } from "features/Collections/data/columns";

function Table({ publicCollections, setCurrentPublicCollection, loading = false, filter }) {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const theme = useTheme();

  const { user } = useSelector((state) => state.auth);

  const noRowsMessage = filter
    ? "No collections found matching your search."
    : "It looks like there are no public collections yet!";

  const [visibilityModel, setVisibilityModel] = useLocalStorage(
    `sooth-api-public-columnvisibility-${user.userId}`,
    publicCollectionsListdefaultVisibilityModel
  );

  const [persistedWidths, setPersistedWidths] = useLocalStorage(
    `sooth-api-public-columnWidths-${user.userId}`,
    {}
  );

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [commonWidth, setCommonWidth] = useState(null);

  const columnWidthsRef = useRef(persistedWidths);

  const computeCommonWidth = () => {
    const element = containerRef.current;
    let width = element ? element.clientWidth : 0;  

    const visibleFields = Object.keys(visibilityModel).filter(
      (field) => visibilityModel[field]
    );
    const unsetCount = visibleFields.length;
    const newCommonWidth = unsetCount > 0 ? Math.floor(width / unsetCount) : 0;
    setCommonWidth(newCommonWidth);
  };

  const debouncedUpdateColumnWidths = () => {
        writeStorage(
          `sooth-api-public-columnWidths-${user.userId}`,
          columnWidthsRef.current
        );
        setPersistedWidths(columnWidthsRef.current);
        computeCommonWidth();
      };

  const handleColumnResize = (params) => {
    columnWidthsRef.current = {
      ...columnWidthsRef.current,
      [params.colDef.field]: params.width,
    };
    debouncedUpdateColumnWidths();
  };

  useEffect(() => {
    if (Object.keys(persistedWidths).length === 0) {
      setCommonWidth(null);
    }
  }, [persistedWidths]);

  useEffect(() => {
    if (!visibilityModel || Object.keys(visibilityModel).length === 0) {
      setVisibilityModel(publicCollectionsListdefaultVisibilityModel);
      writeStorage(
        `sooth-api-public-columnvisibility-${user.userId}`,
        publicCollectionsListdefaultVisibilityModel
      );
      apiRef.current.setColumnVisibilityModel(publicCollectionsListdefaultVisibilityModel);
    } else {
      apiRef.current.setColumnVisibilityModel(visibilityModel);
    }
  }, []);

  const handleVisibilityChange = (newVisibilityModel) => {
    const updatedVisibility = {
      ...visibilityModel,
      ...newVisibilityModel,
    };
    setVisibilityModel(updatedVisibility);
    writeStorage(`sooth-api-public-columnvisibility-${user.userId}`, updatedVisibility);
  };

  const scrollToLeft = () => {
    const virtualScroller = document.querySelector(
      ".MuiDataGrid-virtualScroller"
    );
    if (virtualScroller) {
      virtualScroller.scrollLeft = 0;
    }
  };

  const clearWidthModel = () => {
    scrollToLeft();
    writeStorage(`sooth-api-public-columnWidths-${user.userId}`, {});
    setCommonWidth(null);
    setPersistedWidths({});
    columnWidthsRef.current = {};
  };

  const columns = useColumns({
    fields: publicCollectionsListColumns,
    persistedWidths,
    commonWidth,
    theme,
    defaultFlexes: { collectionName_chr: 2 },
    headerNames: { collectionName_chr: "Collection Name" },
  });

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <ColumnsSection
        visibilityModel={visibilityModel}
        setVisibilityModel={setVisibilityModel}
        columns={columns}
        label={`sooth-api-public-columnvisibility-${user.userId}`}
        clearWidthModel={clearWidthModel}
        widthModal={persistedWidths}
      />
      <CollectionTableComponent
        apiRef={apiRef}
        rows={publicCollections}
        columns={columns}
        handleColumnResize={handleColumnResize}
        visibilityModel={visibilityModel}
        handleVisibilityChange={handleVisibilityChange}
        getRowId={(row) => row.publicCollectionID_chr}
        showCustomEmptyMessage={true}
        onRowClick={(params) => {
          setCurrentPublicCollection(params.row);
          navigate(`/collections/public/${params.row.publicCollectionID_chr}`);
        }}
        loading={loading}
        noRowsMessage={noRowsMessage}
      />
    </Box>
  );
}

export default Table;
